import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, Card, Select, Tab, Typography, useSelect } from '@chilecompra/react-kit/components'
import { getLogger } from '@chilecompra/react-kit/debuggers'
import { DOWNLOADS_MONTH_OPTIONS } from '../../config/settings/constants'
import {
  REACT_APP_MP_URL,
  REACT_APP_OCDS_BASE_URL,
  REACT_APP_OCDS_IMAGE,
  REACT_APP_OCDS_TERMS_URL,
  REACT_APP_OCDS_QUERYTYPE_URL,
  REACT_APP_OCDS_QUERYLIST_URL,
  REACT_APP_OCDS_REPOSITORY_URL,
  REACT_APP_OCDS_METADATA_URL,
  REACT_APP_OCDS_BLOB_URL,
  REACT_APP_OCDS_MP_URL,
  REACT_APP_MP_DEVELOPERS
} from '../../config/settings/environment'
import generateYearsBetween from '../../modules/utils/years'
import DownloadsNavButton from '../../components/Navigation/DownloadsNavButton'
import {
  Clipboard,
  ClipboardAction,
  ClipboardIcon,
  ClipboardText,
  Description,
  DownloadTabs,
  GridContainer,
  GridItem,
  Italic,
  Item,
  Key,
  Label,
  Link,
  List,
  MiniClipboardIcon,
  Root,
  Strong,
  Subtitle,
  Title,
  OCDSimage,
  OCDSimageLink,
  Value
} from './DownloadOCDSSection.styles'

const logger = getLogger('DOWNLOADS_CLIPBOARD')
const START_YEAR = 2009

/**
 * The DownloadOCDSSection's container.
 */
const DownloadOCDSSection = () => {
  const { endYear } = useSelector(state => state.topNavbar)
  const {
    value: yearValue,
    onChange: handleChangeYear,
    onReset: resetYear
  } = useSelect({
    initialValue: endYear || 2022,
    overwriteCallback: () => endYear || 2022
  })
  const { value: monthValue, onChange: handleChangeMonth, onReset: resetMonth } = useSelect({ initialValue: '01' })
  const handleChangeTab = useCallback(() => {
    resetMonth()
    resetYear()
  }, [])
  const handleClipboardFallback = useCallback(content => {
    const textAreaElement = document.createElement('textarea')

    textAreaElement.value = content
    textAreaElement.style.top = '0'
    textAreaElement.style.left = '0'
    textAreaElement.style.position = 'fixed'

    document.body.appendChild(textAreaElement)
    textAreaElement.focus()
    textAreaElement.select()

    try {
      document.execCommand('copy')
    } catch (error) {
      logger.error('Clipboard Fallback: Unable to copy', error)
    }

    document.body.removeChild(textAreaElement)
  }, [])
  const handleDoubleClickClipboard = content => () => {
    if (!window.navigator.clipboard) {
      return handleClipboardFallback(content)
    }

    window.navigator.clipboard
      .writeText(content)
      .then(() => logger.info('Clipboard: copying to clipboard was successful'))
      .catch(error => logger.error('Clipboard: could not copy content', error))
  }
  const handleClickDownload = useCallback(() => {
    const anchorElement = document.createElement('a')
    const month = parseInt(monthValue, 10)
    const year = parseInt(yearValue, 10)
    const url =
      year < 2022 || (year === 2022 && month <= 4)
        ? `${REACT_APP_OCDS_BLOB_URL}/${yearValue}${monthValue}.zip`
        : `${REACT_APP_OCDS_MP_URL}/${yearValue}/${yearValue}${monthValue}.7z`

    anchorElement.setAttribute('download', `${yearValue}${monthValue}`)
    anchorElement.setAttribute('href', url)
    anchorElement.setAttribute('target', '_blank')
    anchorElement.click()
  }, [monthValue, yearValue])
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, START_YEAR), [endYear])
  const isCurrentYear = useMemo(() => yearValue === new Date().getFullYear(), [yearValue])
  const monthOptions = useMemo(
    () =>
      isCurrentYear
        ? DOWNLOADS_MONTH_OPTIONS.filter((_, index) => index <= new Date().getMonth())
        : DOWNLOADS_MONTH_OPTIONS,
    [isCurrentYear]
  )

  return (
    <Root>
      <GridContainer padding="0 0 26px">
        <DownloadsNavButton />
        <GridItem xs={10} sm={11}>
          <Title>Procesos de compra con estándar internacional OCDS</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 43px">
        <GridItem xs={12}>
          <Description>
            El Estándar de Datos para las Contrataciones Abiertas (OCDS por sus siglas en inglés) es un producto central
            de la Organización no Gubernamental{' '}
            <Link href={REACT_APP_OCDS_BASE_URL} target="_blank" rel="noreferrer">
              Open Contracting Partnership (OCP)
            </Link>{' '}
            que permite la divulgación de datos y documentos de todas las{' '}
            <Link href={REACT_APP_MP_DEVELOPERS} target="_blank" rel="noreferrer">
              etapas del proceso de contratación
            </Link>
            , mediante un formato o modelo de datos común. Se creó para apoyar a las organizaciones en la promoción de
            la transparencia de la contratación y para permitir un análisis más profundo de los datos de las compras
            públicas. Puedes conocer más sobre las Políticas de Públicación OCDS en el siguiente{' '}
            <Link href={REACT_APP_OCDS_TERMS_URL} target="_blank" rel="noreferrer">
              enlace
            </Link>
            .
            <Typography fontSize="16px" variant="body1" padding="16px 0 0">
              Un proceso bajo el estándar OCDS se esquematiza de la siguiente manera:
            </Typography>
            <OCDSimage src="/assets/images/esquema-ocds.svg" />
            <OCDSimageLink>
              Conoce en detalle del proceso OCDS{' '}
              <Link href={REACT_APP_OCDS_IMAGE} target="_blank" rel="noreferrer">
                aquí
              </Link>
            </OCDSimageLink>
            <br /> En el contexto del Cuarto Plan de Gobierno Abierto (2018-2020), ChileCompra asumió el compromiso N°
            12 e inició un proceso para incorporar este estándar a todas las etapas de la contratación pública. Este
            compromiso se cumplió el 2020 en un 100%, al sumar a las más de 3,7 millones de fichas de licitaciones
            públicas en <Link href={REACT_APP_MP_URL}>www.mercadopublico.cl</Link>, los 2,2 millones de procesos de
            tratos directos y Convenios Marco. De esta manera, puedes obtener documentos en tiempo real de los procesos
            de compra bajo el estándar OCDS, sin necesidad de registro para los usuarios.
            <br />
            <br />
            ChileCompra fue ganador en diciembre de 2020 del{' '}
            <Strong>Premio al Impacto en la Contratación Abierta</Strong>, por aumentar el acceso y la transparencia en
            las compras públicas través de la implementación del Estándar Abierto de Contratación -también conocido como{' '}
            <Strong>Open Contracting Data Standard</Strong> (OCDS)- en todos los procesos de adquisición de la
            plataforma de compras públicas de Chile <Link href={REACT_APP_MP_URL}>www.mercadopublico.cl</Link>.
            <br />
            <br />
            El Premio al Impacto en la Contratación Abierta es una de las ocho categorías de los Premios a la Excelencia
            e Innovación del WorldCC 2020 que otorgan los organismos multilaterales World Commerce & Contracting
            Association (WCCA) y Open Contracting Partnership (OCP). Reconoce a las personas y organizaciones que logran
            los más altos estándares en la gestión de contratos y a “los reformadores audaces que están modificando la
            imagen en la contratación pública”, gracias a la disposición en formato de datos abiertos de la información
            y de métodos innovadores para mejorar radicalmente las obras, bienes y servicios públicos
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <DownloadTabs value="process" onChange={handleChangeTab}>
            <Tab id="process" label="descarga por proceso">
              <GridContainer padding="0 0 21px">
                <GridItem xs={12}>
                  <Description>
                    Consisten en documentos de procesos de compra en <Strong>formato JSON</Strong>, cuya actualización
                    es en tiempo real. El acceso a los procesos de compra se realiza mediante una consulta a través de
                    URLs específicas:
                  </Description>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 18px">
                <GridItem xs={12}>
                  <Key>Licitación:</Key>
                </GridItem>
                <GridItem xs={12}>
                  <Value
                    onDoubleClick={handleDoubleClickClipboard(
                      `https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMes/{año}/{mes}/{posición_inicial}/{valor_final}`
                    )}
                  >
                    {`https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMes/{año}/{mes}/{posición_inicial}/{valor_final}`}
                    <MiniClipboardIcon />
                  </Value>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 18px">
                <GridItem xs={12}>
                  <Key>Trato directo:</Key>
                </GridItem>
                <GridItem xs={12}>
                  <Value
                    onDoubleClick={handleDoubleClickClipboard(
                      `https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMesTratoDirecto/{año}/{mes}/{posición_inicial}/{valor_final}`
                    )}
                  >
                    {`https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMesTratoDirecto/{año}/{mes}/{posición_inicial}/{valor_final}`}
                    <MiniClipboardIcon />
                  </Value>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 23px">
                <GridItem xs={12}>
                  <Key>Convenio marco:</Key>
                </GridItem>
                <GridItem xs={12}>
                  <Value
                    onDoubleClick={handleDoubleClickClipboard(
                      `https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMesConvenio/{año}/{mes}/{posición_inicial}/{valor_final}`
                    )}
                  >
                    {`https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMesConvenio/{año}/{mes}/{posición_inicial}/{valor_final}`}
                    <MiniClipboardIcon />
                  </Value>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 16px">
                <GridItem xs={12}>
                  <Subtitle>Modo de Uso</Subtitle>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12}>
                  <Description>
                    Para usar cada una de estas consultas, es necesario completar los parámetros que se encuentran
                    encerrados en llaves: año, mes, posición inicial y posición final.
                    <br />
                    <br />
                    La definición de estos parámetros es la siguiente:
                    <br />
                    <br />
                  </Description>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12}>
                  <List>
                    <Item>
                      <Strong>Año</Strong>: Año en que fue publicada la licitación o se envió la orden de compra (Trato
                      Directo y Convenio Marco)
                    </Item>
                    <Item>
                      <Strong>Mes</Strong>: Mes en que fue publicada la licitación o se envió la orden de compra (Trato
                      Directo y Convenio Marco)
                    </Item>
                    <Item>
                      <Strong>Valor inicial</Strong>: Valor mínimo del rango de registros a consultar
                    </Item>
                    <Item>
                      <Strong>Valor final</Strong>: Valor máximo del rango de registros a consultar (máximo 1.000 por
                      consulta)
                    </Item>
                  </List>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 23px">
                <GridItem xs={12}>
                  <Description>
                    <br />
                    Cada consulta posee un rango definido por los parámetros [Valor inicial, Valor final] cuyo valor no
                    puede exceder los mil registros. Considerando esto, para obtener la totalidad de los registros para
                    un año/mes determinado, se debe iterar sobre la consulta modificando el rango [Valor inicial, Valor
                    final] en lotes de mil, hasta completar el total de registros indicado en el resultado de la
                    consulta.
                  </Description>
                </GridItem>
              </GridContainer>
              <GridContainer alignItems="center" padding="0 0 21px">
                <GridItem xs={12}>
                  <Label>Ejemplo de una consulta de Trato Directo:</Label>
                  <Clipboard
                    onDoubleClick={handleDoubleClickClipboard(
                      'https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMesTratoDirecto/2020/01/0/1000'
                    )}
                  >
                    <ClipboardText>
                      https://api.mercadopublico.cl/APISOCDS/OCDS/listaOCDSAgnoMesTratoDirecto/2020/01/0/1000
                    </ClipboardText>
                    <ClipboardAction>
                      <ClipboardIcon />
                    </ClipboardAction>
                  </Clipboard>
                </GridItem>
              </GridContainer>
              <GridContainer alignItems="center" padding="0 0 24px">
                <GridItem xs={12}>
                  <Description>
                    <Italic>
                      Si quisieras extraer todos los datos de enero 2020, entonces la primera consulta tendría como
                      parámetros de valor inicial y final [0,1000], la segunda [1001,2000], y así sucesivamente hasta
                      llegar a cubrir el total de 29.060 registros que indica la consulta.
                    </Italic>
                    <br />
                    <br />
                    Conoce el detalle y los distintos tipos de consultas que puedes realizar:
                  </Description>
                </GridItem>
              </GridContainer>
              <GridContainer alignItems="center" padding="0 0 35px">
                <GridItem xs={12}>
                  <Description>
                    <List>
                      <Item margin="0 0 15px">
                        <Link href={REACT_APP_OCDS_QUERYTYPE_URL} target="_blank" rel="noreferrer">
                          Tipos de consultas disponibles para cada etapa de proceso
                        </Link>
                      </Item>
                      <Item>
                        <Link href={REACT_APP_OCDS_QUERYLIST_URL} target="_blank" rel="noreferrer">
                          Listado de los procesos de compra publicados en un determinado año/mes
                        </Link>
                      </Item>
                    </List>
                  </Description>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12}>
                  <Card color="primary">
                    <GridContainer>
                      <GridItem xs={12}>
                        <List>
                          <Item margin="0 0 15px">
                            En el caso de procesos de licitaciones, se poseen todas las etapas OCDS. En el caso de
                            Tratos Directos y Convenios Marco, solo adjudicación y contrato.
                          </Item>
                          <Item margin="0 0 15px">
                            Las licitaciones poseen datos desde el año 2009, en tanto que de los Convenios Marco y
                            Tratos Directos, desde el año 2019.
                          </Item>
                          <Item margin="0 0 30px">
                            Por el momento los procesos de grandes compras no están incluidos. Se espera su ingreso en
                            un futuro.
                          </Item>
                        </List>
                      </GridItem>
                    </GridContainer>
                  </Card>
                </GridItem>
              </GridContainer>
            </Tab>
            <Tab id="massive" label="descarga masiva de licitaciones">
              <GridContainer padding="0 0 44px">
                <GridItem xs={12}>
                  <Description>
                    Se componen de archivos en <Strong>formato JSON</Strong> comprimidos y agrupados por año y mes.
                    Éstos se actualizan diariamente, agregando en cada archivo las licitaciones publicadas y su avance
                    en etapas.
                    <br />
                    <br />
                    Los archivos corresponden a la categoría "Record", que es un compilado de todas las etapas de
                    proceso de que haya sufrido la licitación: Planning, Tender, Award, Contract e Implementation.
                    <br />
                    <br />
                    Dado que las licitaciones sufren cambios de estado en el tiempo, los archivos mensuales de los
                    últimos 3 años (año presente incluído) <Strong>se actualizan diariamente a las 00:00 horas</Strong>.
                    <br />
                    <br />
                    Cada archivo zip puede pesar aproximadamente entre 500 megabytes a 1.6 gigabytes.
                    <br />
                    <br />
                    En caso de querer descargar los archivos mediante programas, es posible hacerlo mediante URLs en las
                    que se modifique la fecha del archivo de la forma:&nbsp;
                    <Italic>https://ocds.blob.core.windows.net/ocds/yyyymm.zip</Italic>
                  </Description>
                </GridItem>
              </GridContainer>
              <GridContainer alignItems="center" padding="0 0 22px" spacing={2}>
                <GridItem xs={12} md={4}>
                  <Select label="AÑO" options={yearsOptions} value={yearValue} onChange={handleChangeYear} />
                </GridItem>
                <GridItem xs={12} md={4}>
                  <Select label="MES" options={monthOptions} value={monthValue} onChange={handleChangeMonth} />
                </GridItem>
                <GridItem align="right" xs={12} md={4}>
                  <Button color="primary" onClick={handleClickDownload}>
                    Descargar archivo
                  </Button>
                </GridItem>
              </GridContainer>
              <GridContainer padding="0 0 18px">
                <GridItem xs={12}>
                  <Description>
                    Además, accede a repositorios de ejemplo e información relacionada los metadatos de los archivos:
                  </Description>
                </GridItem>
              </GridContainer>
              <GridContainer>
                <GridItem xs={12}>
                  <List>
                    <Item margin="0 0 15px">
                      <Link href={REACT_APP_OCDS_REPOSITORY_URL} target="_blank" rel="noreferrer">
                        Repositorio con prototipo de ejemplo para descargar, almacenar y visualizar procesos de compra
                        mediante el estándar OCDS
                      </Link>
                    </Item>
                    <Item>
                      <Link href={REACT_APP_OCDS_METADATA_URL} target="_blank" rel="noreferrer">
                        Metadata de los campos contenidos dentro del estándar
                      </Link>
                    </Item>
                  </List>
                </GridItem>
              </GridContainer>
            </Tab>
          </DownloadTabs>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default DownloadOCDSSection
